<template>
  <div>
    <mdb-card v-if="roundStatus == 'Ready for Timer'">
      <mdb-card-header class="secondary-color"><h4>Ready?</h4></mdb-card-header>
      <mdb-card-body>
        <div v-if="currentState.iAmModerator">
          <p>Hit <b>Start</b> below when you and the other players are ready to begin the round.</p>
          <mdb-btn icon="clock" @click="$emit('setStatus', 'Timer Running')">Start</mdb-btn>
        </div>
        <div v-if="!game.iAmModerator">
          <span class="spinner-grow text-warning"/>
          <span class="text-warning">Waiting on {{ currentState.moderatorName }} to start the round.</span>
        </div>
      </mdb-card-body>
    </mdb-card>

    <mdb-card v-if="roundStatus == 'Time Up'">
      <mdb-card-header v-if="timeLeft <= 0" class="secondary-color"><h4>⌚ Time's Up!</h4></mdb-card-header>
      <div>
        <span class="spinner-grow text-warning"/>
        <span v-if="!currentState.iAmModerator" class="text-warning"> Waiting on {{ currentState.moderatorName }} to
          evaluate answers.</span>
      </div>
    </mdb-card>

    <mdb-card v-if="roundStatus == 'Timer Running'" class="mb-2">
      <mdb-card-header v-if="timeLeft > 0"><h4>⌚ Find Some Words! 🔎</h4>
      </mdb-card-header>

      <mdb-card-body>
        <div v-if="timeLeft > 0 ">
          <mdb-progress :value="progressValue"/>
        </div>
      </mdb-card-body>
    </mdb-card>

    <div v-if="roundStatus == 'Timer Running'">
      <mdb-card  class="mb-2">
        <mdb-card-body class="text-center">
          <mdb-btn color="warning" rounded size="sm" @click="clearAnswer">Clear</mdb-btn>
          <mdb-btn :disabled="!currAnswer || currAnswer.length < 3 " color="success" rounded size="sm"
                   @click="acceptAnswer">Accept
          </mdb-btn>
          <div class="h3">{{ displayAnswer }}</div>
        </mdb-card-body>
      </mdb-card>

      <mdb-row>
        <mdb-col col="12" lg="6" md="8" sm="12">
          <mdb-card>
            <mdb-card-body class="p-4">
              <mdb-tbl bordered class="word-grid" sm striped>
                <mdb-tbl-body>
                  <tr v-for="n in 5">
                    <td v-for="(l,i) in currentState.grid" v-if="i >= (n*6) && i< (( n+1) *6)"
                        :id="'cell-' + i" class="h4 clickable-cell" @click="clickCell(i)">{{ l }}
                    </td>
                  </tr>
                </mdb-tbl-body>
              </mdb-tbl>
              <div class="text-info small">
                Click/tap letters to form words. Letters must touch horizontally, vertically or diagonally. Minimum: 3 letters.
              </div>
            </mdb-card-body>
          </mdb-card>
        </mdb-col>
        <mdb-col col="12" lg="6" md="4" sm="12">
          <mdb-card>
            <mdb-card-body class="text-left">
              <div class="h4 text-info strong">My Words</div>
              <div v-for="(a,i) in myAnswers" :key="i">
                {{ a }}
              </div>
            </mdb-card-body>
          </mdb-card>
        </mdb-col>
      </mdb-row>

    </div>

    <mdb-card v-if="currentState.iAmModerator &&  roundStatus == 'Time Up'"
              class="mt-4">
      <mdb-card-body>
        <h4>Ready to Evaluate Round?</h4>
        <div class="text-info">
          Make sure everyone's timer is up first.
        </div>
        <div>
          <mdb-btn color="success" icon="check" @click="$emit('setStatus', 'Evaluate Round')">Evaluate!</mdb-btn>
        </div>
      </mdb-card-body>
    </mdb-card>

    <mdb-card v-if="currentState.currentStatus == 'Evaluate Round'"
              class="mt-4 text-left">
      <mdb-card-body>
        <h4>Round Evaluation</h4>
        <div class="text-info" v-if="currentState.iAmModerator">
          <p>Review the answers with the other players. As the moderator of this round, you can toggle an answer's validity by
            tapping/clicking it. Players will only get points for the green answers.</p>
          <p>When you're done, click "Score Round" below.</p>
        </div>
        <div v-if="!currentState.iAmModerator">
          <span class="spinner-grow text-warning"/>
          <span class="text-warning">{{ currentState.moderatorName }} is reviewing the answers.</span>
        </div>
        <mdb-card>
          <mdb-card-header class="primary-color-dark text-white">
            <h3>Answers
              <mdb-btn color="secondary" size="sm" rounded icon="table" @click="boardModal=true">View Board</mdb-btn>
            </h3>
          </mdb-card-header>
          <mdb-card-body v-if="playersLocal">
            <div v-for="p in playersLocal" :key="p.id">
              <div class="text-info h4">{{ p.name }}</div>
              <mdb-badge v-if="p.currentAnswer && p.currentAnswer.length == 0" color="warning">None</mdb-badge>
              <a v-for="(ans,j) in p.currentAnswer " v-if="p.currentAnswer && p.currentAnswer.length>0"
                 href="" text="white" @click.prevent="toggleValid(ans)">
                <mdb-chip :color="ans.valid ? 'success-color' : 'danger-color'"
                          text="white">
                  {{ ans.answer }}
                </mdb-chip>
              </a>
            </div>
          </mdb-card-body>
        </mdb-card>
        <mdb-btn v-if="currentState.iAmModerator" color="success" icon="check" @click="scoreRound">Score Round</mdb-btn>

      </mdb-card-body>
    </mdb-card>

    <scoreboard v-if="currentState.currentStatus == 'Review Scores'"
                :currentState="currentState" :game="game"/>

    <mdb-modal v-if="boardModal" size="lg" @close="boardModal = false">
      <mdb-modal-header :close="true" class="primary-color text-center text-white">
        <h5>Original Playing Board</h5>
      </mdb-modal-header>
      <mdb-modal-body>
        <mdb-card-body class="p-4">
          <mdb-tbl bordered striped>
            <mdb-tbl-body>
              <tr v-for="n in 5">
                <td v-for="(l,i) in currentState.grid" v-if="i >= (n*6) && i< (( n+1) *6)"
                    :id="'cell-' + i" class="h4">{{ l }}
                </td>
              </tr>
            </mdb-tbl-body>
          </mdb-tbl>
        </mdb-card-body>
      </mdb-modal-body>
    </mdb-modal>




  </div>
</template>
<script>
import Avatar from "../common/Avatar";
import {
  mdbBadge,
  mdbBtn,
  mdbBtnFixed,
  mdbBtnFixedItem,
  mdbCard,
  mdbCardBody,
  mdbCardFooter,
  mdbCardHeader,
  mdbChip,
  mdbChipInput,
  mdbCol,
  mdbContainer,
  mdbIcon,
  mdbInput,
  mdbJumbotron,
  mdbMask,
  mdbModal,
  mdbModalBody,
  mdbModalHeader,
  mdbModalTitle,
  mdbProgress,
  mdbRow,
  mdbSpinner,
  mdbTbl,
  mdbTblBody,
  mdbTblHead
} from 'mdbvue';
import WordCards from "./WordCards";
import WordCard from "./WordCard";
import Scoreboard from "./Scoreboard";
import {custChipInput} from "../common/CustChipInput";
import api from "../../services/groupsPlaceApi";
import moment from "moment";

export default {
  name: "campfire-tutti-frutti-play",
  components: {
    mdbContainer,
    mdbBtn,
    mdbCard,
    mdbCardHeader,
    mdbCardBody,
    mdbCardFooter,
    mdbChip,
    mdbIcon,
    mdbInput,
    mdbModal,
    mdbModalHeader,
    mdbModalBody,
    mdbModalTitle,
    mdbChipInput,
    mdbMask,
    mdbBadge,
    mdbSpinner,
    mdbJumbotron,
    mdbBtnFixed,
    mdbBtnFixedItem,
    mdbProgress,
    mdbRow,
    mdbCol,
    mdbTbl,
    mdbTblHead,
    mdbTblBody,
    Avatar,
    WordCards,
    WordCard,
    Scoreboard,
    custChipInput
  },
  watch: {
    roundStatus(n, o) {
      if (n == "Timer Running") {
        this.myAnswers = []
        this.currCells = []
        this.lastClicked = null
        this.monitorTime()
      }
      if (n == "Evaluate Round") {
        this.fetchPlayers()
      }
    },
    /*myAnswers(n, o) {
     console.log("My answers....", n)
     }*/

  },
  computed: {
    currAnswer() {
      let ret = ""
      for (let j of this.currCells) {
        ret += this.currentState.grid[j]
      }
      return ret
    },
    displayAnswer() {
      let ret = this.currAnswer
      if (!ret) ret = "✖ ✖ ✖"
      if (ret.length == 1) ret += "✖✖"
      if (ret.length == 2) ret += "✖"
      return ret
    },
    loggedIn() {
      return this.$store.state.loggedIn
    },
    currentUser() {
      return this.$store.state.userDetails
    },
    settings() {
      return this.$store.state.settings
    },
    lastError() {
      if (!this.game) return null
      else return this.game.lastError
    },
    roundStatus() {
      return this.currentState.currentStatus
    },
    players() {
      return this.currentState.players
    }
  },
  props: {
    currentState: {
      type: Object,
      default: null
    },
    game: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      progressValue: 0,
      timeLeft: 10,
      myAnswers: null,
      playersLocal: null,
      lastClicked: null,
      currCells: [],
      boardModal: false
    }
  },
  mounted() {
    if (this.roundStatus == "Timer Running") this.monitorTime()
    if (this.roundStatus == "Evaluate Round") {
      this.prepareAnswers(this.currentState.players)
    }
  },
  methods: {
    acceptCell(i) {
      let el = document.getElementById("cell-" + i)
      if (!el) return
      this.lastClicked = i
      this.currCells.push(i)
      el.classList.add("primary-color-dark")
      el.classList.add("text-white")
    },
    clickCell(i) {
      //first click
      if (Object.is(this.lastClicked, null) || Object.is(this.lastClicked, undefined)) {
        this.acceptCell(i)
        return
      }

      //undo last
      if (this.lastClicked === i) {
        if (this.currCells.length > 0) {
          this.currCells.pop()
          this.lastClicked = this.currCells[this.currCells.length - 1]
        }
        if (this.currCells.length == 0) this.lastClicked = null
        let el = document.getElementById("cell-" + i)
        if (!el) return

        el.classList.remove("primary-color-dark")
        el.classList.remove("text-white")
        return
      }

      if (this.currCells.includes(i)) {
        //already picked and not the last one.
        return
      }


      let lastRow = Math.floor(this.lastClicked / 6)
      let thisRow = Math.floor(i / 6)

      //touching?
      if (i == this.lastClicked + 1 && lastRow == thisRow) this.acceptCell(i)
      if (i == this.lastClicked - 1 && lastRow == thisRow) this.acceptCell(i)
      if (i == this.lastClicked + 5 && thisRow == lastRow + 1) this.acceptCell(i)
      if (i == this.lastClicked + 6 && thisRow == lastRow + 1) this.acceptCell(i)
      if (i == this.lastClicked + 7 && thisRow == lastRow + 1) this.acceptCell(i)
      if (i == this.lastClicked - 5 && thisRow == lastRow - 1) this.acceptCell(i)
      if (i == this.lastClicked - 6 && thisRow == lastRow - 1) this.acceptCell(i)
      if (i == this.lastClicked - 7 && thisRow == lastRow - 1) this.acceptCell(i)
    },
    clearCells() {
      for (let i of this.currCells) {
        let el = document.getElementById("cell-" + i)
        if (el) {
          el.classList.remove("primary-color-dark")
          el.classList.remove("text-white")
        }
      }
    },
    clearAnswer() {
      this.clearCells()
      this.lastClicked = null
      this.currCells = []
    },
    acceptAnswer() {
      if (!this.myAnswers) this.myAnswers = []
      for (let ans of this.myAnswers) {
        if (ans == this.currAnswer) {
          this.$notify.error({message: "You already used that word.", timeOut: 2000})
          return
        }
      }
      this.myAnswers.push(this.currAnswer)
      this.clearCells()
      this.lastClicked = null
      this.currCells = []
    },
    toggleValid(ans) {
      if (!this.currentState.iAmModerator) return
      ans.valid = !ans.valid
    },
    monitorTime() {
      if (this.roundStatus != "Timer Running") return
      this.timeLeft = (moment(this.currentState.timeUp)).diff(moment()) / 1000
      if (this.timeLeft <= 0) this.progressValue = 100
      else {
        this.progressValue = Math.round((this.timeLeft / 120) * 100)
      }
      if (this.timeLeft > 0) {
        setTimeout(() => {
          this.monitorTime()
        }, 500)
      } else {
        this.currentState.currentStatus = "Time Up"
        this.submitAnswers()
      }
    },
    scoreRound() {
      let scores = []
      for (let p of this.playersLocal) {
        let row = {player: p.playerId, score: 0}
        for (let ans of p.currentAnswer) {
          if (ans.valid) {
            if (ans.answer.length == 3) row.score += 1
            if (ans.answer.length == 4) row.score += 2
            else row.score += ((ans.answer.length - 3) * 2)
          }
        }
        scores.push(row)
      }
      this.$store.dispatch("setExecuting", true)
      api.post("/campfirePlayer/submittedScore/" + this.game.sessionId, {
        accessToken: this.game.accessToken,
        scores: scores
      }).then(
        (results) => {

        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)

      })
    },
    submitAnswers() {
      if (!this.myAnswers || this.myAnswers.length == 0) return
      this.$store.dispatch("setExecuting", true)
      api.post("/campfirePlayer/answer/" + this.game.sessionId, {
        accessToken: this.game.accessToken,
        answer: this.myAnswers
      }).then(
        (results) => {
          this.myAnswer = null
          this.currentState.iAnswered = true
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)

      })
    },
    fetchPlayers() {
      //if (!this.currentState.iAmModerator) return
      this.$store.dispatch("setExecuting", true)
      api.post("/campfirePlayer/players/" + this.game.sessionId, {
        accessToken: this.game.accessToken
      }).then(
        (results) => {
          this.prepareAnswers(results.data)
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)

      })
    },
    prepareAnswers(players) {
      this.playersLocal = []
      if (!players) return
      for (let p of players) {
        let pl = JSON.parse(JSON.stringify(p))
        let a = JSON.parse(p.currentAnswer)
        pl.currentAnswer = []
        if (a) {
          for (let ans of a) {
            let newAnswer = {answer: ans, valid: true}
            pl.currentAnswer.push(newAnswer)
          }
        }
        this.playersLocal.push(pl)
      }
      for (let p of this.playersLocal) {
        for (let ans of p.currentAnswer) {
          if (ans.valid) {
            if (this.findDuplicate(ans.answer, p.playerId)) ans.valid = false
          }
        }
      }
    },
    findDuplicate(answer, ignore) {
      let ret = false
      for (let p of this.playersLocal) {
        if (ignore != p.playerId) {
          for (let a of p.currentAnswer) {
            if (a.answer && a.answer.toLowerCase() == answer.toLowerCase()) ret = true
          }
        }
      }
      return ret
    }
  }
};
</script>
<style scoped>
.clickable-cell {
  cursor: pointer;
}

@media screen and (min-width: 750px ) {
  .word-grid {
    max-width: 75%;
    margin: auto;
  }

  .word-grid td {
    padding: .8em;
  }
}

/*@media screen and (max-width: 749px ) {
  .selected-word {
    display: none;
  }

}*/

</style>
